import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  DateTimeInput,
  FileDropZone,
  Modal,
  TimezoneSelect,
  getDeviceTimezone,
  normalizeTimeZone,
  timezoneLabel,
} from '@proaxiv/ui'
import { Button } from '../components/Button'
import { ColorPickerButton } from '../components/ColorPickerButton'
import { DEFAULT_BRANDING, addEvent, updateEvent as updateEventSlice } from '../store/eventsSlice'
import type { AppEvent, EventBranding, UploadedFile } from '../store/eventsSlice'
import { useAppDispatch } from '../store/hooks'
import { fileToUploadedFile, CURATED_FONTS, loadGoogleFont } from '../utils/event'
import { countryToTimezone } from '../utils/countries'
import {
  useCreateEventMutation,
  useGetEventQuery,
  useGetOrgQuery,
  useUpdateEventMutation,
  getApiToken,
  type GetOrganizationResponse,
  type UpdateEventBody,
} from '@proaxiv/server-api'
import { useActiveOrg } from '../contexts/ActiveOrgContext'
import { toCreateEventBody, toUpdateEventBody, fromEventSummary } from './event/eventMapping'
import type { GetEventResponse, CreateEventResponse } from './event/eventMapping'
import { PublicEventView } from './event/PublicEventView'
import { RegistrationFormView } from './event/RegistrationFormView'
import { TicketView } from './event/TicketView'
import { ReminderEmailView } from './event/ReminderEmailView'
import { CertificateView } from './event/CertificateView'
import type { Registration } from '../store/registrationsSlice'
import i18n from 'i18next'

/**
 * Builds the fixed mock participant used only by the ticket-preview tab, since a real
 * registration doesn't exist yet while an event is still being drafted. A realistic name/email
 * (rather than generic placeholders like "—") makes the preview read as a genuine ticket
 * instead of an obviously-broken one.
 *
 * This must be computed from the reactive `t` returned by `useTranslation()` inside the
 * component (not a module-level constant calling the global `i18n.t()` directly): translations
 * are fetched asynchronously from the backend, so a one-time call at module-eval time would run
 * before the `events` namespace has loaded and permanently bake in the raw, untranslated keys.
 */
function buildTicketPreviewRegistration(t: (key: string) => string): Registration {
  return {
    id: 'preview',
    eventId: 'preview',
    secretToken: 'preview',
    firstName: t('ep_preview_participant_name'),
    email: t('ep_preview_participant_email'),
    company: '',
    consentPersonalData: true,
    consentMedia: false,
    status: 'accepted',
    registeredAt: '',
    registeredByOrganizer: false,
    role: 'Audience',
  }
}

// ── Preview tab icons ──────────────────────────────────────────────────────────
// Paired with a short text label on every tab (never icon-only) — purely a recognition
// aid so organizers can tell the four previews apart at a glance instead of having to
// read each label every time (COGA: familiar, non-mystery icons alongside literal text).

function PreviewTabIconWebPage() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18" />
    </svg>
  )
}

function PreviewTabIconForm() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  )
}

function PreviewTabIconTicket() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8z" />
      <path d="M12 6v2M12 16v2M12 11v2" />
    </svg>
  )
}

function PreviewTabIconEnvelope() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  )
}

function PreviewTabIconCertificate() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="6" />
      <path d="M9 13.5 7.5 22 12 19.5 16.5 22 15 13.5" />
    </svg>
  )
}

/**
 * The five live-preview tabs, in the same order a real visitor experiences them (event page →
 * registration form → ticket → reminder email → post-event certificate) — a familiar,
 * predictable order beats an arbitrary one and means organizers never have to hunt for "the
 * next step" preview.
 */
const PREVIEW_TABS = [
  { key: 'event', labelKey: 'ep_preview_tab_event', Icon: PreviewTabIconWebPage },
  { key: 'registration', labelKey: 'ep_preview_tab_registration', Icon: PreviewTabIconForm },
  { key: 'ticket', labelKey: 'ep_preview_tab_ticket', Icon: PreviewTabIconTicket },
  { key: 'reminder', labelKey: 'ep_preview_tab_reminder', Icon: PreviewTabIconEnvelope },
  { key: 'certificate', labelKey: 'ep_preview_tab_coa', Icon: PreviewTabIconCertificate },
] as const

// ── Error helpers ──────────────────────────────────────────────────────────────

/**
 * Extracts a human-readable message from an RTK Query error.
 * The backend's error body is `{ code, message, timestamp }` (see engine
 * `ApiError`), so `err.data` is an object — never stringify it directly or
 * you get the literal text "[object Object]".
 *
 * This is a module-level (non-component) helper, so it resolves i18n copy via the
 * `i18next` singleton (`i18n.t`) rather than the `useTranslation` hook.
 */
function extractErrorMessage(err: unknown, fallback: string): string {
  if (err == null || typeof err !== 'object' || !('data' in err)) return fallback
  const data = (err as { data?: unknown }).data
  if (data == null) return fallback
  if (typeof data === 'string') return data

  if (typeof data === 'object' && 'message' in data) {
    const message = String((data as { message?: unknown }).message)
    // Quota-exceeded events come back as "Quota exceeded for feature
    // 'eventplanner': limit is 5" — reword it into a friendlier message.
    const quotaMatch = message.match(/limit is (\d+)/i)
    if (quotaMatch) {
      return i18n.t('ep_quota_limit', { count: Number(quotaMatch[1]) })
    }
    return message || fallback
  }
  return fallback
}

// ── Image upload helpers ──────────────────────────────────────────────────────

/** Converts a base64 data URL to a Blob for multipart upload. */
function dataUrlToBlob(dataUrl: string, mimeType: string): Blob {
  const [, base64] = dataUrl.split(',')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Blob([bytes], { type: mimeType })
}

/**
 * Uploads a logo or flyer image to the backend via multipart POST.
 * Skips files whose dataUrl is already a server URL (not a fresh base64 pick).
 * Silently ignores network/server errors — images are best-effort after the event is saved —
 * but always returns the actual persisted S3 key (or `null` when nothing was uploaded/it failed)
 * so the caller can build a cache-busting URL instead of guessing.
 */
async function uploadEventImage(
  eventId: string,
  file: UploadedFile,
  type: 'logo' | 'flyer'
): Promise<string | null> {
  // dataUrl starting with '/api' or 'http' means it came from the server — nothing new to upload
  if (!file.dataUrl.startsWith('data:')) return null
  const token = getApiToken()
  if (!token) return null
  const blob = dataUrlToBlob(file.dataUrl, file.mimeType)
  const form = new FormData()
  form.append('file', blob, file.name)
  try {
    const res = await fetch(`/api/v1/events/${eventId}/${type}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    })
    if (!res.ok) return null
    const body = (await res.json()) as { s3Key?: string }
    return body.s3Key ?? null
  } catch {
    return null
  }
}

/**
 * Deletes a previously uploaded logo or flyer from the backend.
 * Silently ignores errors — best-effort, same as the upload counterpart.
 */
async function deleteEventImage(eventId: string, type: 'logo' | 'flyer'): Promise<void> {
  const token = getApiToken()
  if (!token) return
  await fetch(`/api/v1/events/${eventId}/${type}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
}

// ── Small field components ────────────────────────────────────────────────────

function Field({
  label,
  id,
  hint,
  required,
  children,
}: {
  label: string
  id: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-pax-black text-sm font-medium">
        {label}
        {required && <span className="text-pax-red ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-pax-gray text-xs">{hint}</p>}
    </div>
  )
}

const inputCls =
  'border-pax-purple/30 text-pax-black placeholder:text-pax-gray/50 focus:border-pax-blue w-full rounded-xl border bg-white px-3 py-2 text-sm transition-colors outline-none'

function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={inputCls} />
}

function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${inputCls} min-h-[120px] resize-y`} />
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-pax-black border-pax-purple/20 border-b pb-2 text-xs font-bold tracking-widest uppercase">
      {children}
    </p>
  )
}

// ── Branding panel ────────────────────────────────────────────────────────────

function BrandingPanel({
  branding,
  onChange,
}: {
  branding: EventBranding
  onChange: (b: EventBranding) => void
}) {
  const { t } = useTranslation()

  useEffect(() => {
    loadGoogleFont(branding.headingFont)
    loadGoogleFont(branding.bodyFont)
  }, [branding.headingFont, branding.bodyFont])

  function set<K extends keyof EventBranding>(key: K, value: EventBranding[K]) {
    onChange({ ...branding, [key]: value })
  }

  return (
    <div className="flex flex-col gap-5">
      <FileDropZone
        label={t('ep_field_logo')}
        accept="image/*"
        current={branding.logo}
        preview
        uploadLabel={t('file_upload')}
        onFileSelected={async (file) => set('logo', await fileToUploadedFile(file))}
        onRemove={() => set('logo', undefined)}
      />

      {/* Colors */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {(
          [
            { key: 'primary', label: t('ep_color_primary') },
            { key: 'background', label: t('ep_color_background') },
            { key: 'text', label: t('ep_color_text') },
            { key: 'accent', label: t('ep_color_accent') },
          ] as { key: keyof EventBranding; label: string }[]
        ).map(({ key, label }) => (
          <div key={key} className="flex items-center gap-2">
            <ColorPickerButton
              value={branding[key] as string}
              onChange={(color) => set(key, color)}
              label={label}
            />
            <div className="flex flex-col gap-0.5">
              <span className="text-pax-black text-xs font-medium">{label}</span>
              <span className="text-pax-gray font-mono text-[10px]">{branding[key] as string}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Fonts */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t('ep_font_heading')} id="heading-font">
          <select
            id="heading-font"
            value={branding.headingFont}
            onChange={(e) => set('headingFont', e.target.value)}
            className={inputCls}
          >
            {CURATED_FONTS.map((f) => (
              <option key={f.family} value={f.family}>
                {f.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('ep_font_body')} id="body-font">
          <select
            id="body-font"
            value={branding.bodyFont}
            onChange={(e) => set('bodyFont', e.target.value)}
            className={inputCls}
          >
            {CURATED_FONTS.map((f) => (
              <option key={f.family} value={f.family}>
                {f.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

/** Create / edit event wizard. */
export function EventEditorPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const ticketPreviewRegistration = useMemo(() => buildTicketPreviewRegistration(t), [t])

  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const { activeOrgId: orgId } = useActiveOrg()

  // ── API hooks ──────────────────────────────────────────────────────────────
  // Always call the API for edit mode — never trust the Redux cache alone.
  // The backend may now return 403 if the org's eventplanner feature has been
  // disabled since the event was cached (TC-EVT-004 fix).
  const {
    data: getEventRaw,
    isLoading: loadingExisting,
    error: getEventError,
  } = useGetEventQuery({ eventId: id! }, { skip: !isEdit || !id })
  const [createEvent, { isLoading: creating, error: createError }] = useCreateEventMutation()
  const [updateEvent, { isLoading: updating, error: updateError }] = useUpdateEventMutation()
  const isGetEventForbidden =
    getEventError != null &&
    'status' in getEventError &&
    (getEventError.status === 403 || getEventError.status === 'PARSING_ERROR')

  const existing =
    getEventRaw != null ? fromEventSummary((getEventRaw as GetEventResponse).event) : undefined

  // ── Form state (initialised from backend once loaded) ─────────────────────
  const [title, setTitle] = useState(existing?.title ?? '')
  const [status, setStatus] = useState<AppEvent['status']>(existing?.status ?? 'draft')
  const [address, setAddress] = useState(existing?.address ?? '')
  const [startDateTime, setStartDateTime] = useState(existing?.startDateTime ?? '')
  const [endDateTime, setEndDateTime] = useState(existing?.endDateTime ?? '')
  const [text, setText] = useState(existing?.text ?? '')
  const [ticketAnnouncement, setTicketAnnouncement] = useState(existing?.ticketAnnouncement ?? '')
  const [poster, setPoster] = useState<UploadedFile | undefined>(existing?.poster)
  const [ticketActivation, setTicketActivation] = useState(existing?.ticketActivation ?? '')
  // Mirrors the registration-status pattern: a plain-language radio choice instead of an
  // empty-vs-filled date field standing in for the decision. Kept as separate state (rather than
  // derived from `ticketActivation === ''`) so picking "from a specific date & time" reveals the
  // picker immediately, before the organizer has actually chosen a date.
  const [ticketActivationMode, setTicketActivationMode] = useState<'start' | 'scheduled'>(
    existing?.ticketActivation ? 'scheduled' : 'start'
  )
  const [registrationClosesAt, setRegistrationClosesAt] = useState(
    existing?.registrationClosesAt ?? ''
  )
  // New events default to registration closed until the organiser explicitly opens it
  // (TC-EVT-012); existing events keep whatever state they were saved with.
  const [registrationManualState, setRegistrationManualState] = useState<
    AppEvent['registrationManualState']
  >(isEdit ? existing?.registrationManualState : 'closed')
  const [autoReminderAt, setAutoReminderAt] = useState(existing?.autoReminderAt ?? '')
  const [branding, setBranding] = useState<EventBranding>(existing?.branding ?? DEFAULT_BRANDING)
  // Timezone used to interpret/display the wall-clock values entered above — defaults to the
  // organizer's device timezone for new events, or the saved value for existing ones. Overridden
  // below (once) by the organization's registered country, if the organizer hasn't already
  // changed it, so the initial guess is usually right and rarely needs touching (TC-EVT-058).
  // Normalized first so an exotic device identifier (e.g. Etc/GMT-9) never reaches display or
  // persistence (TC-EVT-061).
  const [timezone, setTimezone] = useState(
    normalizeTimeZone(existing?.timezone ?? getDeviceTimezone())
  )
  // Whether the "Change timezone" modal is open — the picker stays collapsed into a
  // plain-language summary by default because almost every organizer never needs to touch this
  // field (issue: a bare dropdown up front made the whole form look like it required a decision
  // it usually doesn't). The modal opens with the picker already interactive, in one click,
  // instead of a two-step reveal-then-open-the-dropdown flow.
  const [timezoneModalOpen, setTimezoneModalOpen] = useState(false)
  // Draft value edited inside the modal — kept separate from `timezone` so "Cancel" can discard
  // it without leaving the saved value in a half-changed state.
  const [draftTimezone, setDraftTimezone] = useState(timezone)
  // True once the organizer has explicitly picked a timezone themselves — after that, the
  // org-country default below must never silently overwrite their choice.
  const timezoneTouchedRef = useRef(false)

  // Fetch the active organization to preselect the timezone from its registered country.
  const { data: orgRaw } = useGetOrgQuery({ orgId: orgId! }, { skip: !orgId })
  const org = orgRaw as GetOrganizationResponse | undefined
  useEffect(() => {
    if (isEdit || timezoneTouchedRef.current) return
    const countryTimezone = countryToTimezone(org?.address?.country)
    if (countryTimezone) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time default seeded from an async org fetch, not a value derivable at render time
      setTimezone(countryTimezone)
    }
  }, [isEdit, org?.address?.country])

  // Re-populate form once backend data loads (edit mode)
  const seededRef = useRef(false)
  useEffect(() => {
    if (existing && !seededRef.current) {
      seededRef.current = true
      setTitle(existing.title)
      setStatus(existing.status)
      setAddress(existing.address)
      setStartDateTime(existing.startDateTime)
      setEndDateTime(existing.endDateTime ?? '')
      setText(existing.text)
      setTicketAnnouncement(existing.ticketAnnouncement ?? '')
      setPoster(existing.poster)
      setTicketActivation(existing.ticketActivation)
      setTicketActivationMode(existing.ticketActivation ? 'scheduled' : 'start')
      setRegistrationClosesAt(existing.registrationClosesAt ?? '')
      setRegistrationManualState(existing.registrationManualState)
      setAutoReminderAt(existing.autoReminderAt ?? '')
      setBranding(existing.branding)
      setTimezone(normalizeTimeZone(existing.timezone ?? getDeviceTimezone()))
    }
  }, [existing])

  const [saveAttempted, setSaveAttempted] = useState(false)
  const [apiError, setApiError] = useState<string | null>(null)
  // RTK Query's `isLoading` only flips after the next render, which leaves a brief window where
  // a fast double-click fires two `handleSave` calls before the Save button is actually disabled
  // — each one creating its own event (see #223). This ref is set synchronously on the very
  // first click, so a second click occurring before React re-renders is still rejected.
  const saveInFlightRef = useRef(false)

  const isSaving = creating || updating
  const isBusy = isSaving
  const cancelTo = isEdit && id ? `/event-planner/${id}` : '/event-planner'

  function buildPayload(): Omit<AppEvent, 'id' | 'createdAt'> {
    return {
      title: title.trim(),
      status,
      address,
      startDateTime,
      endDateTime: endDateTime || undefined,
      text,
      ticketAnnouncement: ticketAnnouncement || undefined,
      poster,
      ticketActivation,
      registrationClosesAt: registrationClosesAt || undefined,
      registrationManualState,
      autoReminderAt: autoReminderAt || undefined,
      timezone,
      branding,
    }
  }

  async function handleSave() {
    // Synchronous re-entrancy guard — see saveInFlightRef above for why this can't rely on
    // isBusy/isSaving alone.
    if (saveInFlightRef.current) return
    saveInFlightRef.current = true
    try {
      await doSave()
    } finally {
      saveInFlightRef.current = false
    }
  }

  async function doSave() {
    setSaveAttempted(true)
    setApiError(null)
    if (!title.trim() || !startDateTime || !endDateTime) return

    const payload = buildPayload()

    try {
      let savedEventId: string | undefined

      if (isEdit && id) {
        await updateEvent({
          eventId: id,
          updateEventBody: toUpdateEventBody(payload),
        }).unwrap()
        savedEventId = id
        // Keep the local Redux store in sync so navigating back shows fresh data
        dispatch(updateEventSlice({ ...payload, id, createdAt: existing?.createdAt ?? '' }))
      } else {
        if (!orgId) {
          setApiError(t('ep_err_no_org'))
          return
        }
        const result = (await createEvent({
          createEventBody: toCreateEventBody(payload, orgId),
        }).unwrap()) as CreateEventResponse

        savedEventId = result.eventId

        // The create endpoint always sets DRAFT + registration closed by default (TC-EVT-012),
        // so follow up with a PATCH for anything the organiser explicitly chose that differs
        // from those defaults.
        const followUp: Pick<UpdateEventBody, 'status' | 'registrationManualState'> = {}
        if (status === 'published') followUp.status = 'PUBLISHED'
        if (registrationManualState === 'open') followUp.registrationManualState = 'OPEN'
        else if (registrationManualState === 'closed') followUp.registrationManualState = 'CLOSED'
        else if (registrationManualState === 'hidden') followUp.registrationManualState = 'HIDDEN'
        else followUp.registrationManualState = 'AUTO'
        if (savedEventId && Object.keys(followUp).length > 0) {
          await updateEvent({
            eventId: savedEventId,
            updateEventBody: followUp,
          }).unwrap()
        }

        // Sync the new event into the local Redux store
        if (savedEventId) {
          dispatch(
            addEvent({
              ...payload,
              id: savedEventId,
              status: status === 'published' ? 'published' : 'draft',
              createdAt: new Date().toISOString(),
            })
          )
        }
      }

      // Sync images after the event exists on the backend (best-effort, non-blocking):
      // upload when a new/changed file is set, or delete when a previously-saved asset was removed.
      if (savedEventId) {
        const logoUpload = branding.logo
          ? uploadEventImage(savedEventId, branding.logo, 'logo')
          : Promise.resolve(null)
        const flyerUpload = poster
          ? uploadEventImage(savedEventId, poster, 'flyer')
          : Promise.resolve(null)
        if (!branding.logo && existing?.branding.logo) void deleteEventImage(savedEventId, 'logo')
        if (!poster && existing?.poster) void deleteEventImage(savedEventId, 'flyer')
        const [logoResult, flyerResult] = await Promise.allSettled([logoUpload, flyerUpload])
        const newLogoKey = logoResult.status === 'fulfilled' ? logoResult.value : null
        const newFlyerKey = flyerResult.status === 'fulfilled' ? flyerResult.value : null

        // Once persisted, always reference logo/poster via the stable backend URL rather than
        // whatever shape happened to be in local state (a raw base64 blob for a freshly-picked
        // file, or a passthrough URL for an unchanged one). Without this, re-saving an event
        // without touching its poster would overwrite the Redux cache's working base64 preview
        // with a URL reference before the upload had actually round-tripped, and any consumer
        // that reads from the cache (e.g. the public page for published events) could end up
        // with a stale/inconsistent value instead of the freshly persisted asset.
        //
        // The URL also carries the freshly returned S3 key as a `?v=` cache-busting param —
        // that path is otherwise identical on every request, so without it a browser/CDN that
        // already cached the previous image would keep serving stale bytes after a replace,
        // and removing/re-adding an asset could look like nothing changed at all.
        dispatch(
          updateEventSlice({
            id: savedEventId,
            poster: poster
              ? {
                  ...poster,
                  dataUrl: newFlyerKey
                    ? `/api/v1/public/events/${savedEventId}/flyer?v=${encodeURIComponent(newFlyerKey)}`
                    : `/api/v1/public/events/${savedEventId}/flyer`,
                }
              : undefined,
            branding: {
              ...branding,
              logo: branding.logo
                ? {
                    ...branding.logo,
                    dataUrl: newLogoKey
                      ? `/api/v1/public/events/${savedEventId}/logo?v=${encodeURIComponent(newLogoKey)}`
                      : `/api/v1/public/events/${savedEventId}/logo`,
                  }
                : undefined,
            },
          })
        )
      }

      if (savedEventId) {
        navigate(`/event-planner/${savedEventId}`)
      } else {
        navigate('/event-planner')
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, t('reg_err_generic')))
    }
  }

  // ── Live preview (debounced) ────────────────────────────────────────────────
  // Fed to PublicEventView so organizers see a true WYSIWYG result of the public event
  // page as they type — instead of imagining it from field labels alone (the root cause
  // of the confusion reports this redesign addresses). Updates shortly after a change
  // rather than on every keystroke, and never animates: a calm, non-distracting preview
  // per COGA guidance against unnecessary movement/extraneous cognitive load.
  const buildPreviewEvent = (): AppEvent => {
    const payload = buildPayload()
    return {
      ...payload,
      id: id ?? 'preview',
      createdAt: existing?.createdAt ?? new Date().toISOString(),
      // Until the organizer types a title, show the same example that the input uses as
      // its placeholder — an empty heading in the live preview looks broken, and the
      // placeholder is already the product's example of what a title looks like.
      title: payload.title || t('ep_field_title_placeholder'),
    }
  }
  const [previewEvent, setPreviewEvent] = useState<AppEvent>(buildPreviewEvent)
  useEffect(() => {
    const timer = setTimeout(() => setPreviewEvent(buildPreviewEvent()), 400)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    status,
    address,
    startDateTime,
    endDateTime,
    text,
    ticketAnnouncement,
    poster,
    ticketActivation,
    registrationClosesAt,
    registrationManualState,
    autoReminderAt,
    timezone,
    branding,
  ])
  // Which of the five live previews the organizer is currently looking at — the public
  // event page (default), the registration form, the participant ticket, the reminder
  // email, or the post-event certificate. A tab switch, rather than showing all five at
  // once, keeps the pane to one clear thing at a time (familiar pattern, no extra scrolling
  // to compare full-length previews).
  const [previewTab, setPreviewTab] = useState<
    'event' | 'registration' | 'ticket' | 'reminder' | 'certificate'
  >('event')
  const saveDisabled = !title.trim() || !startDateTime || !endDateTime
  const saveDisabledReason = useMemo(() => {
    if (!title.trim() && !startDateTime) return t('ep_save_disabled_reason_both')
    if (!title.trim()) return t('ep_save_disabled_reason_title')
    if (!startDateTime) return t('ep_save_disabled_reason_start')
    if (!endDateTime) return t('ep_save_disabled_reason_end')
    return null
  }, [title, startDateTime, endDateTime, t])

  // The public event page's natural height varies a lot (poster, address, announcements...)
  // and can easily exceed the viewport. The pane itself scrolls independently on desktop
  // (see `lg:overflow-y-auto` below) so the preview stays fully readable at its real size,
  // rather than being shrunk down to an illegibly small scale to force it to fit.
  const previewContainerRef = useRef<HTMLDivElement>(null)

  // Mobile jump links: each one only makes sense — and should only appear — while its
  // *target* pane is scrolled out of view (e.g. "View the live preview" is pointless, and
  // was confusingly shown, while the preview pane is already on screen). "Visible" starts
  // false (unconfirmed) for both panes, so both links show until the observer's first
  // callback proves one pane is actually on screen — and stay showing in test environments
  // without IntersectionObserver, which fall back to the old always-show behavior.
  //
  // A plain area-based threshold (e.g. "25% of the pane is on screen") is the wrong test here:
  // it's a ratio of the *pane's own* height, and both the form and the preview pane are
  // routinely taller than the viewport, so that ratio can stay under any reasonable threshold
  // even while the user is looking squarely at one of them — leaving both links stuck visible
  // forever. Instead we watch whether each pane currently contains the vertical center of the
  // viewport (via a zero-height `rootMargin` line through the middle) — a check whose result
  // doesn't depend on how tall the pane itself is.
  const formPaneRef = useRef<HTMLDivElement>(null)
  const [formPaneVisible, setFormPaneVisible] = useState(false)
  const [previewPaneVisible, setPreviewPaneVisible] = useState(false)
  useEffect(() => {
    const formEl = formPaneRef.current
    const previewEl = previewContainerRef.current
    if (!formEl || !previewEl || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === formEl) setFormPaneVisible(entry.isIntersecting)
          if (entry.target === previewEl) setPreviewPaneVisible(entry.isIntersecting)
        }
      },
      { threshold: 0, rootMargin: '-50% 0px -50% 0px' }
    )
    observer.observe(formEl)
    observer.observe(previewEl)
    return () => observer.disconnect()
    // Re-run whenever the loading/403/404 guards below (which each early-return *before* the
    // main JSX — and therefore before `ep-form-pane`/`ep-preview-pane` ever mount) might flip:
    // with an empty dep array this ran once on the *first* render, when edit mode is still
    // showing the loading skeleton and both refs are still null, permanently skipping observer
    // setup — leaving both jump buttons stuck visible forever, exactly the reported bug. Re-running
    // once the real panes exist attaches the observer to the elements that actually exist now.
  }, [isEdit, loadingExisting, isGetEventForbidden, existing])

  // Loading skeleton for edit mode
  if (isEdit && loadingExisting) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 md:px-6 md:py-8">
        <div className="border-pax-purple/20 animate-pulse rounded-2xl border bg-white p-12 text-center">
          <div className="bg-pax-purple/10 mx-auto h-6 w-48 rounded" />
        </div>
      </div>
    )
  }

  // Guard: 403 — feature disabled for this org (TC-EVT-004)
  if (isEdit && isGetEventForbidden) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 md:px-6 md:py-8">
        <Link
          to="/event-planner"
          className="text-pax-gray hover:text-pax-blue mb-8 flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
        >
          ← {t('ep_title')}
        </Link>
        <div className="border-pax-purple/20 flex flex-col items-center gap-4 rounded-2xl border bg-white p-12 text-center">
          <p className="text-pax-gray text-5xl font-black opacity-20">403</p>
          <p className="text-pax-black text-lg font-semibold">{t('ep_403_title')}</p>
          <p className="text-pax-gray text-sm">{t('ep_403_body')}</p>
        </div>
      </div>
    )
  }

  // Guard: edit route with an unknown id should not silently no-op
  if (isEdit && !loadingExisting && !existing) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-6 md:px-6 md:py-8">
        <Link
          to="/event-planner"
          className="text-pax-gray hover:text-pax-blue mb-8 flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
        >
          ← {t('ep_title')}
        </Link>
        <div className="border-pax-purple/20 flex flex-col items-center gap-4 rounded-2xl border bg-white p-12 text-center">
          <p className="text-pax-gray text-5xl font-black opacity-20">404</p>
          <p className="text-pax-black text-lg font-semibold">{t('ep_404_title')}</p>
          <p className="text-pax-gray text-sm">{t('ep_404_body')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      {/* Breadcrumb — links back to the event's management page when editing an
           existing event (so organizers return to where they came from), or to the
           Event Planner list when creating a new one (there's no event to go back to). */}
      <Link
        to={isEdit && id ? `/event-planner/${id}` : '/event-planner'}
        className="text-pax-gray hover:text-pax-blue flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        {isEdit ? t('ep_editor_back_to_event') : t('ep_title')}
      </Link>

      {/* Heading */}
      <div className="mt-6 mb-6 md:mt-8 md:mb-8">
        <h1 className="text-pax-black text-2xl font-bold tracking-tight md:text-3xl">
          {isEdit ? t('ep_editor_title_edit') : t('ep_editor_title_new')}
        </h1>
        <p className="text-pax-gray mt-1 text-sm">{t('ep_editor_subtitle')}</p>
      </div>

      {/* Two-pane layout: the form is the primary column on every viewport; the live
          preview sits alongside it on wide screens and stacks below it on narrow ones.
          The floating jump controls (below, fixed to the viewport) are mobile-only
          ("recognition, not recall"): unlike inline links that scroll away with the
          content, these stay on their own layer above the page so a way back to the
          other pane is always on screen, no matter how far the user has scrolled. */}
      <div className="fixed top-20 right-4 z-30 flex flex-col items-end gap-2 lg:hidden">
        {!previewPaneVisible && (
          <a
            href="#ep-preview-pane"
            className="text-pax-blue ring-pax-purple/15 flex min-h-11 items-center justify-center gap-2 rounded-full bg-white px-4 text-sm font-semibold shadow-lg ring-1 transition-colors hover:opacity-90 focus-visible:outline-none"
          >
            {t('ep_preview_jump')}
            <span aria-hidden="true">↓</span>
          </a>
        )}
        {!formPaneVisible && (
          <a
            href="#ep-form-pane"
            className="text-pax-blue ring-pax-purple/15 flex min-h-11 items-center justify-center gap-2 rounded-full bg-white px-4 text-sm font-semibold shadow-lg ring-1 transition-colors hover:opacity-90 focus-visible:outline-none"
          >
            <span aria-hidden="true">↑</span>
            {t('ep_form_jump')}
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
        <div
          id="ep-form-pane"
          ref={formPaneRef}
          className="border-pax-purple/25 rounded-2xl border bg-white p-6 shadow-sm"
        >
          <div className="flex flex-col gap-6">
            {/* ── Event details ── */}
            <SectionTitle>{t('ep_section_details')}</SectionTitle>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Timezone — surfaced first, in its own callout, because it governs every date
                and time entered anywhere in this form (start/end, ticket activation,
                registration deadline, reminder), not just the fields immediately below it.
                Collapsed to a plain-language summary by default (issue: a bare dropdown made
                the whole form look like it required a decision it almost never does) — it is
                preselected from the organization's registered country, so most organizers never
                need to open it. */}
              <div className="border-pax-blue/20 bg-pax-light-blue/40 flex flex-col gap-2 rounded-xl border p-4 sm:col-span-2">
                <p className="text-pax-black text-sm font-medium">
                  {t('ep_tz_summary', {
                    zone: timezoneLabel(timezone),
                  })}
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setDraftTimezone(timezone)
                    setTimezoneModalOpen(true)
                  }}
                  className="w-fit"
                >
                  {t('ep_tz_change')}
                </Button>
              </div>

              <div className="sm:col-span-2">
                <Field label={t('ep_field_title')} id="ev-title" required>
                  <TextInput
                    id="ev-title"
                    value={title}
                    placeholder={t('ep_field_title_placeholder')}
                    onChange={(e) => setTitle(e.target.value)}
                    aria-invalid={saveAttempted && !title.trim()}
                    className={saveAttempted && !title.trim() ? 'border-pax-red!' : ''}
                  />
                </Field>
                {saveAttempted && !title.trim() && (
                  <p className="text-pax-red mt-1 text-xs font-medium">
                    {t('ep_err_title_required')}
                  </p>
                )}
              </div>

              <div>
                <Field label={t('ep_field_start')} id="ev-start" required>
                  <DateTimeInput
                    id="ev-start"
                    value={startDateTime}
                    timezone={timezone}
                    onChange={setStartDateTime}
                    aria-invalid={saveAttempted && !startDateTime}
                    className={saveAttempted && !startDateTime ? 'border-pax-red!' : inputCls}
                  />
                </Field>
                {saveAttempted && !startDateTime && (
                  <p className="text-pax-red mt-1 text-xs font-medium">
                    {t('ep_err_start_required')}
                  </p>
                )}
              </div>

              <div>
                <Field label={t('ep_field_end')} id="ev-end" required>
                  <DateTimeInput
                    id="ev-end"
                    value={endDateTime}
                    timezone={timezone}
                    onChange={setEndDateTime}
                    aria-invalid={saveAttempted && !endDateTime}
                    className={saveAttempted && !endDateTime ? 'border-pax-red!' : inputCls}
                  />
                </Field>
                {saveAttempted && !endDateTime && (
                  <p className="text-pax-red mt-1 text-xs font-medium">
                    {t('ep_err_end_required')}
                  </p>
                )}
              </div>

              <div className="sm:col-span-2">
                <Field
                  label={t('ep_field_address')}
                  id="ev-address"
                  hint={t('ep_field_address_hint')}
                >
                  <TextInput
                    id="ev-address"
                    value={address}
                    placeholder={t('ep_field_address_placeholder')}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field label={t('ep_field_text')} id="ev-text" hint={t('ep_field_text_hint')}>
                  <Textarea
                    id="ev-text"
                    value={text}
                    placeholder={t('ep_field_text_placeholder')}
                    onChange={(e) => setText(e.target.value)}
                  />
                </Field>
              </div>

              <div className="sm:col-span-2">
                <Field
                  label={t('ep_field_ticket_announcement')}
                  id="ev-ticket-announcement"
                  hint={t('ep_field_ticket_announcement_hint')}
                >
                  <Textarea
                    id="ev-ticket-announcement"
                    value={ticketAnnouncement}
                    placeholder={t('ep_field_ticket_announcement_placeholder')}
                    onChange={(e) => setTicketAnnouncement(e.target.value)}
                  />
                </Field>
              </div>
            </div>

            {/* ── Visibility ── */}
            <SectionTitle>{t('ep_section_status')}</SectionTitle>

            {/* Plain-language radio group, matching the Registration pattern below — one
              sentence per option instead of two bare "Draft"/"Published" toggle buttons with
              no explanation of what each one actually does. */}
            <div
              role="radiogroup"
              aria-labelledby="ev-status-label"
              className="flex flex-col gap-2"
            >
              <p id="ev-status-label" className="text-pax-black text-sm font-medium">
                {t('ep_field_status')}
              </p>
              <div className="flex flex-col gap-2">
                {(
                  [
                    {
                      value: 'draft' as const,
                      label: t('ep_status_draft'),
                      desc: t('ep_status_draft_desc'),
                    },
                    {
                      value: 'published' as const,
                      label: t('ep_status_published'),
                      desc: t('ep_status_published_desc'),
                    },
                  ] as { value: AppEvent['status']; label: string; desc: string }[]
                ).map(({ value, label, desc }) => {
                  const optionId = `ev-status-${value}`
                  const checked = status === value
                  return (
                    <label
                      key={optionId}
                      htmlFor={optionId}
                      className={[
                        'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
                        checked
                          ? 'border-pax-blue bg-pax-light-blue/40'
                          : 'border-pax-purple/30 hover:bg-pax-light-purple',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        id={optionId}
                        name="ev-status"
                        checked={checked}
                        onChange={() => setStatus(value)}
                        className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-current"
                      />
                      <span className="flex flex-col gap-0.5">
                        <span className="text-pax-black text-sm font-semibold">{label}</span>
                        <span className="text-pax-gray text-xs">{desc}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* ── Poster ── */}
            <SectionTitle>{t('ep_section_poster')}</SectionTitle>
            <FileDropZone
              label={t('ep_field_poster')}
              accept="image/*"
              current={poster}
              preview
              uploadLabel={t('file_upload')}
              onFileSelected={async (file) => setPoster(await fileToUploadedFile(file))}
              onRemove={() => setPoster(undefined)}
            />

            {/* ── QR code activation ── */}
            <SectionTitle>{t('ep_section_ticket')}</SectionTitle>

            {/* Same plain-language radio pattern as the registration status below — one
              sentence per option, so the organizer recognizes the right choice instead of
              inferring it from whether a date field happens to be empty. */}
            <div
              role="radiogroup"
              aria-labelledby="ev-activation-mode-label"
              className="flex flex-col gap-2"
            >
              <p id="ev-activation-mode-label" className="text-pax-black text-sm font-medium">
                {t('ep_field_activation_mode')}
              </p>
              <div className="flex flex-col gap-2">
                {(
                  [
                    {
                      value: 'start' as const,
                      label: t('ep_activation_mode_start'),
                      desc: t('ep_activation_mode_start_desc'),
                    },
                    {
                      value: 'scheduled' as const,
                      label: t('ep_activation_mode_scheduled'),
                      desc: t('ep_activation_mode_scheduled_desc'),
                    },
                  ] as { value: 'start' | 'scheduled'; label: string; desc: string }[]
                ).map(({ value, label, desc }) => {
                  const optionId = `ev-activation-mode-${value}`
                  const checked = ticketActivationMode === value
                  return (
                    <label
                      key={optionId}
                      htmlFor={optionId}
                      className={[
                        'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
                        checked
                          ? 'border-pax-blue bg-pax-light-blue/40'
                          : 'border-pax-purple/30 hover:bg-pax-light-purple',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        id={optionId}
                        name="ev-activation-mode"
                        checked={checked}
                        onChange={() => {
                          setTicketActivationMode(value)
                          if (value === 'start') setTicketActivation('')
                        }}
                        className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-current"
                      />
                      <span className="flex flex-col gap-0.5">
                        <span className="text-pax-black text-sm font-semibold">{label}</span>
                        <span className="text-pax-gray text-xs">{desc}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Date field — only shown for the "specific date & time" option, mirroring the
              registration closure date below (conditional reveal keeps the screen minimal). */}
            {ticketActivationMode === 'scheduled' && (
              <Field
                label={t('ep_field_activation')}
                id="ev-activation"
                hint={t('ep_field_activation_hint')}
              >
                <DateTimeInput
                  id="ev-activation"
                  value={ticketActivation}
                  timezone={timezone}
                  onChange={setTicketActivation}
                  className={inputCls}
                />
              </Field>
            )}

            {/* ── Registration ── */}
            <SectionTitle>{t('ep_section_registration')}</SectionTitle>

            {/* Plain-language radio group — one sentence per option, so the organizer recognizes
              the right choice instead of having to recall what each label means (issue: the
              4-state toggle buttons "Auto/Open/Closed/Hidden" were reported as confusing). */}
            <div
              role="radiogroup"
              aria-labelledby="ev-reg-state-label"
              className="flex flex-col gap-2"
            >
              <p id="ev-reg-state-label" className="text-pax-black text-sm font-medium">
                {t('ep_field_reg_manual_state')}
              </p>
              <div className="flex flex-col gap-2">
                {(
                  [
                    {
                      value: 'open' as const,
                      label: t('ep_reg_state_open'),
                      desc: t('ep_reg_open_desc'),
                    },
                    {
                      value: undefined,
                      label: t('ep_reg_state_auto'),
                      desc: t('ep_reg_auto_desc'),
                    },
                    {
                      value: 'closed' as const,
                      label: t('ep_reg_state_closed'),
                      desc: t('ep_reg_closed_desc'),
                    },
                    {
                      value: 'hidden' as const,
                      label: t('ep_reg_state_hidden'),
                      desc: t('ep_reg_hidden_desc'),
                    },
                  ] as { value: AppEvent['registrationManualState']; label: string; desc: string }[]
                ).map(({ value, label, desc }) => {
                  const optionId = `ev-reg-state-${String(value)}`
                  const checked = registrationManualState === value
                  return (
                    <label
                      key={optionId}
                      htmlFor={optionId}
                      className={[
                        'flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors',
                        checked
                          ? 'border-pax-blue bg-pax-light-blue/40'
                          : 'border-pax-purple/30 hover:bg-pax-light-purple',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        id={optionId}
                        name="ev-reg-state"
                        checked={checked}
                        onChange={() => setRegistrationManualState(value)}
                        className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-current"
                      />
                      <span className="flex flex-col gap-0.5">
                        <span className="text-pax-black text-sm font-semibold">{label}</span>
                        <span className="text-pax-gray text-xs">{desc}</span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>

            {/* Closure date — only shown for the "closes automatically on a date" option, since
              it has no effect for the other three (conditional reveal keeps the screen minimal
              and prevents "why is this field here?" confusion). */}
            {registrationManualState === undefined && (
              <Field
                label={t('ep_field_reg_closes_at')}
                id="ev-reg-closes"
                hint={t('ep_field_reg_closes_at_hint')}
              >
                <DateTimeInput
                  id="ev-reg-closes"
                  value={registrationClosesAt}
                  timezone={timezone}
                  onChange={setRegistrationClosesAt}
                  className={inputCls}
                />
              </Field>
            )}

            {/* ── Participation reminders ── */}
            <SectionTitle>{t('ep_section_reminders')}</SectionTitle>
            <Field
              label={t('ep_field_auto_reminder')}
              id="ev-auto-reminder"
              hint={t('ep_field_auto_reminder_hint')}
            >
              <DateTimeInput
                id="ev-auto-reminder"
                value={autoReminderAt}
                timezone={timezone}
                onChange={setAutoReminderAt}
                className={inputCls}
              />
            </Field>

            {/* ── Public page appearance ── */}
            <SectionTitle>{t('ep_section_appearance')}</SectionTitle>
            <BrandingPanel branding={branding} onChange={setBranding} />

            {/* ── API error ── */}
            {(apiError ?? (createError || updateError ? t('ep_err_request_failed') : null)) && (
              <p role="alert" className="text-pax-red rounded-xl bg-red-50 px-4 py-3 text-sm">
                {apiError ?? t('ep_err_request_failed')}
              </p>
            )}

            {/* ── Actions — sticky so "Save" stays reachable while scrolling this long form ── */}
            <div className="border-pax-purple/20 sticky bottom-0 -mx-6 -mb-6 flex flex-col items-end gap-1.5 border-t bg-white/95 px-6 py-4 backdrop-blur supports-[backdrop-filter]:bg-white/85">
              <div className="flex items-center justify-end gap-3">
                <Button variant="secondary" onClick={() => navigate(cancelTo)} disabled={isBusy}>
                  {t('btn_cancel')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    void handleSave()
                  }}
                  disabled={saveDisabled || isBusy}
                  aria-describedby={saveDisabledReason ? 'ev-save-disabled-reason' : undefined}
                >
                  {isSaving ? t('btn_saving') : t('btn_save')}
                </Button>
              </div>
              {/* Explains exactly what will enable the button (COGA: a disabled control must say
                why, never leave the user guessing) — reflects live as fields are filled in. */}
              {saveDisabledReason && (
                <p
                  id="ev-save-disabled-reason"
                  role="status"
                  aria-live="polite"
                  className="text-pax-gray text-xs"
                >
                  {saveDisabledReason}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Live preview pane ── */}
        <div
          id="ep-preview-pane"
          ref={previewContainerRef}
          className="lg:border-pax-purple/25 lg:sticky lg:top-6 lg:flex lg:h-[calc(100vh-3rem)] lg:flex-col lg:overflow-hidden lg:rounded-2xl lg:border lg:shadow-sm"
        >
          <div className="border-pax-purple/25 bg-pax-black flex flex-col gap-2 rounded-t-2xl border-x border-t px-4 py-2.5 lg:border-0">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-white/30" aria-hidden="true" />
              <p className="text-xs font-medium text-white/70">{t('ep_preview_title')}</p>
            </div>
            {/* Tabs — a familiar, always-visible way to switch which preview is shown,
                instead of stacking all five full-length previews in the same pane. Laid out
                as a fixed 3-column grid (rather than a single row that would wrap
                unpredictably as tabs were added) so every tab keeps the same size and
                position no matter how narrow the pane gets — an icon paired with its label
                speeds up recognition without relying on a mystery icon alone. */}
            <div
              role="tablist"
              aria-label={t('ep_preview_title')}
              className="grid grid-cols-3 gap-1.5"
            >
              {PREVIEW_TABS.map(({ key, labelKey, Icon }) => (
                <Button
                  key={key}
                  type="button"
                  variant="unstyled"
                  role="tab"
                  id={`ep-preview-tab-${key}`}
                  aria-selected={previewTab === key}
                  aria-controls="ep-preview-panel"
                  onClick={() => setPreviewTab(key)}
                  className={`flex min-h-8 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none ${
                    previewTab === key
                      ? 'text-pax-black bg-white'
                      : 'text-white/60 hover:text-white/90'
                  }`}
                >
                  <Icon />
                  {t(labelKey)}
                </Button>
              ))}
            </div>
          </div>
          <div
            id="ep-preview-panel"
            role="tabpanel"
            aria-labelledby={`ep-preview-tab-${previewTab}`}
            className="border-pax-purple/25 rounded-b-2xl border-x border-b lg:flex-1 lg:overflow-y-auto lg:rounded-b-2xl lg:border"
          >
            {previewTab === 'event' && <PublicEventView event={previewEvent} previewMode />}
            {previewTab === 'registration' && (
              <RegistrationFormView event={previewEvent} previewMode />
            )}
            {previewTab === 'ticket' && (
              <TicketView
                event={previewEvent}
                registration={ticketPreviewRegistration}
                previewMode
              />
            )}
            {previewTab === 'reminder' && (
              <ReminderEmailView
                event={previewEvent}
                participantName={ticketPreviewRegistration.firstName}
                participantEmail={ticketPreviewRegistration.email}
              />
            )}
            {previewTab === 'certificate' && <CertificateView event={previewEvent} />}
          </div>
        </div>
      </div>

      {/* Timezone picker — opens as a modal (rather than an inline reveal) so picking a value
          takes one click instead of two (reveal the field, then open its own dropdown), and so
          "Cancel" can back out without leaving the saved timezone half-changed. */}
      {timezoneModalOpen && (
        <Modal
          labelledBy="ev-timezone-modal-title"
          onBackdropClick={() => setTimezoneModalOpen(false)}
        >
          <div className="border-pax-purple/50 flex w-full max-w-md flex-col rounded-2xl border bg-white shadow-2xl ring-1 ring-black/5">
            <div className="border-pax-purple/20 flex items-center justify-between border-b px-6 py-4">
              <h2 id="ev-timezone-modal-title" className="text-pax-black text-lg font-bold">
                {t('ep_tz_modal_title')}
              </h2>
              <button
                type="button"
                onClick={() => setTimezoneModalOpen(false)}
                className="text-pax-gray hover:text-pax-black cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
                aria-label={t('btn_close')}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-3 px-6 py-5">
              <Field label={t('ep_field_timezone')} id="ev-timezone">
                <TimezoneSelect
                  id="ev-timezone"
                  value={draftTimezone}
                  onChange={setDraftTimezone}
                  className={inputCls}
                  autoFocus
                />
              </Field>
              <p className="text-pax-blue text-xs font-medium">{t('ep_field_timezone_hint')}</p>
            </div>
            <div className="border-pax-purple/20 flex items-center justify-end gap-3 border-t px-6 py-4">
              <Button variant="secondary" size="sm" onClick={() => setTimezoneModalOpen(false)}>
                {t('btn_cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  timezoneTouchedRef.current = true
                  setTimezone(draftTimezone)
                  setTimezoneModalOpen(false)
                }}
              >
                {t('btn_confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
